/**
 * Token Refresh Utilities for V1
 *
 * Provides automatic token refresh for V2 API calls when the access_token expires.
 * Adapted from V2's views_shared/app/javascript/api/withTokenRefresh.js (kept in sync with it).
 *
 * Usage:
 * import { fetchWithTokenRefresh } from "javascript/fetchWithTokenRefresh";
 *
 * const response = await fetchWithTokenRefresh(url, {
 *   method: "POST",
 *   body: JSON.stringify(data),
 * });
 *
 * if (!response.ok) {
 *   throw new Error(`API error: ${response.status}`);
 * }
 *
 * The utility handles:
 * - Automatic retry with refreshed token on 401
 * - Redirect to login if session is invalid
 * - No need to check for 401 in calling code
 */
import _ from 'lodash';

import vlog from './vlog';
import { authenticatedHeadersHash, accessToken } from './v2_authentication';

/**
 * Get CSRF token from Rails meta tag
 * @returns {string|null} The CSRF token or null if not found
 */
export const getCSRFToken = () => document.querySelector("meta[name='csrf-token']")?.getAttribute('content');

/**
 * Refresh the access token by calling the Rails refresh endpoint. It only
 * allows one refresh request in flight.
 *
 * This uses the server-side refresh token (stored in Rails session)
 * to get a new access_token cookie. The refresh token is never exposed
 * to JavaScript for security reasons.
 *
 * @returns {Promise<boolean>} true if refresh succeeded, false if user needs to re-login
 */
let inFlightAccessTokenRefreshRequest = null;
export const refreshAccessToken = async () => {
  let requestOwner = false;
  try {
    if (!inFlightAccessTokenRefreshRequest) {
      requestOwner = true;
      inFlightAccessTokenRefreshRequest = fetch('/session_api/refresh_tokens', {
        method: 'POST',
        headers: {
          'X-CSRF-Token': getCSRFToken(),
          'Content-Type': 'application/json',
        },
        credentials: 'same-origin', // Include cookies
      });
    }

    const response = await inFlightAccessTokenRefreshRequest;

    if (response.ok) {
      return true; // New access_token cookie has been set
    }

    if (response.status === 401) {
      // Session is invalid - user needs to re-login
      return false;
    }

    // Other errors (500, network, etc)
    vlog.error('Token refresh failed:', response.status, response.statusText);
    return false;
  } catch (error) {
    // Network errors, etc
    vlog.error('Token refresh error:', error);
    return false;
  } finally {
    if (requestOwner) {
      inFlightAccessTokenRefreshRequest = null;
    }
  }
};

/* High level fetchWithTokenRefresh helper functions */
const throwSessionExpiredError = () => {
  throw new Error('Session expired, redirecting to login');
};

const handleAuthFailure = (redirectOnAuthFailure, fallback) => {
  // Feature specs have no Supabase session — bouncing the page to "/" would swap the
  // page under the test, and a thrown rejection would wedge callers that only handle
  // {data, error} results. Resolve a plain 401 so call sites take their normal error
  // paths (same pattern as v2HealthCheck's test bypass).
  if (window.RAILS_ENV === 'test') {
    return new Response(null, { status: 401, statusText: 'Unauthorized (test env: no V2 session)' });
  }
  if (redirectOnAuthFailure) {
    vlog.warn('Authentication failed, redirecting to login');
    window.location.href = '/';
    throwSessionExpiredError();
  }
  return fallback();
};

const doAuthenticatedFetch = (url, options) => {
  const headers = new Headers({ ...authenticatedHeadersHash(), ...options.headers });
  return fetch(url, { ...options, headers });
};

/**
 * Wraps a fetch call with automatic token refresh on 401 Unauthorized.
 *
 * When the access_token expires (after ~1 hour), this function:
 * 1. Catches the 401 error
 * 2. Calls the refresh endpoint to get a new token
 * 3. Retries the request with the new token
 * 4. If refresh fails, redirects to login page
 *
 * This handles ALL auth failure cases in one place - calling code doesn't
 * need to check for 401 or handle redirects.
 *
 * @param {string} url - The URL to fetch
 * @param {RequestInit} options - Fetch options (method, body, etc.)
 * @param {Object} config - Configuration options
 * @param {boolean} config.redirectOnAuthFailure - Whether to redirect to login on auth failure (default: true)
 * @returns {Promise<Response>} The fetch response
 *
 * @example
 * // Simple usage - handles everything automatically
 * const response = await fetchWithTokenRefresh(url, {
 *   method: "POST",
 *   body: JSON.stringify(data),
 * });
 *
 * // Just check for success and handle errors
 * if (!response.ok) {
 *   throw new Error(`API error: ${response.status}`);
 * }
 */
export const fetchWithTokenRefresh = async (url, options = {}, { redirectOnAuthFailure = true } = {}) => {
  if (!accessToken()) {
    vlog.info('No access token on file (long expired?), attempting token refresh');
    const refreshed = await refreshAccessToken();
    if (!refreshed) return handleAuthFailure(redirectOnAuthFailure, throwSessionExpiredError);
  }

  const response = await doAuthenticatedFetch(url, options);
  if (response.status !== 401) return response;

  vlog.info('Received 401 from PostgREST, attempting token refresh');
  const refreshed = await refreshAccessToken();
  if (!refreshed) return handleAuthFailure(redirectOnAuthFailure, _.constant(response));

  vlog.info('Token refreshed, retrying request');
  const retryResponse = await doAuthenticatedFetch(url, options);
  if (retryResponse.status === 401) return handleAuthFailure(redirectOnAuthFailure, _.constant(retryResponse));

  return retryResponse;
};
